// YYYY-MM-DD DEPRECATED
import { addDays, startOfISOWeek } from "date-fns";

// DD/MM/YYYY
export const getFormattedFrDate = (date: Date) => {
    return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

export const getFirstDayOfWeek = (date: Date): Date => {
    const dayOfWeek = date.getDay(); // 0 (dimanche) à 6 (samedi)
    const firstDay = new Date(date);
    firstDay.setDate(date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));

    return firstDay;
};

// YYYY-MM-DD
export const getFormattedDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

/** Normalise proSince (API ou listing) en valeur input date YYYY-MM-DD. */
export const formatProSinceInputValue = (
    value?: Date | string | null,
): string | null => {
    if (!value) return null;

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;

    return getFormattedDateInput(parsed);
};

export const parseInputDateToLocal = (value: string): Date | null => {
    if (!value) return null;

    const [year, month, day] = value.split("-").map(Number);
    if (
        [year, month, day].some((part) => Number.isNaN(part)) ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31
    ) {
        return null;
    }

    const parsed = new Date(year, month - 1, day);

    if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day
    ) {
        return null;
    }

    return parsed;
};

export const isWeekendInputDate = (value: Date | string): boolean => {
    let parsedDate: Date | null = null;
    
    if (typeof value === "string") {
        parsedDate = parseInputDateToLocal(value);
    } else {
        parsedDate = value;
    }
    
    if (!parsedDate) {
        return false;
    }

    const day = parsedDate.getDay();
    return day === 0 || day === 6;
};

// YYYY-MM
export const getEnNumericMonthYear = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
};

// Month YYYY in French
export const getMonthYearLabel = (date: Date) => {
    const dateString = date.toLocaleDateString("fr-FR", {
        month: "long",
        year: "numeric",
    });

    return dateString.replace(/^./, (c) => c.toUpperCase());
};

// Convert YYYY-MM-DD to DD/MM/YYYY
export const getFrenchDateFromString = (dateString: string) => {
    const date =
        parseInputDateToLocal(dateString) ?? new Date(dateString);
    return getFormattedFrDate(date);
};

export const isWithinSessionEditWindow = (date: Date) => {
    return new Date(date) > new Date(Date.now() - 15 * 24 * 60 * 60 * 1000);
};

const SESSION_DELETE_WINDOW_MS = 48 * 60 * 60 * 1000;

/** True si la session peut encore être supprimée (fenêtre 48 h après création). */
export const isWithinSessionDeleteWindow = (session: {
    date: string;
    createdAt?: Date | string;
}): boolean => {
    const ref = session.createdAt
        ? new Date(session.createdAt).getTime()
        : parseInputDateToLocal(session.date)?.getTime();
    if (ref == null) return true;
    return Date.now() - ref < SESSION_DELETE_WINDOW_MS;
};

// DD MMMM YYYY
export const getShortFrDate = (date: Date) => {
    return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

// DD/MM/YY
export const getShortDateFormat = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
};

// DD MMMM YYYY
export const getFullFrDate = (date: Date) => {
    return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
};

export const getFirstDayOfSpecificMonth = (
    year: number,
    month: number
): Date => {
    return new Date(year, month, 1);
};

export const getLastDayOfSpecificMonth = (
    year: number,
    month: number
): Date => {
    return new Date(year, month + 1, 0);
};

// New calendar
export const getFirstDayOfSpecificYear = (year: number): Date => {
    return new Date(year, 0, 1);
};

export const getLastDayOfSpecificYear = (year: number): Date => {
    return new Date(year, 11, 31);
};

const isSameCalendarMonth = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

/** Le bouton Valider appartient au mois du vendredi, dernier jour de trading. */
export const canShowWeekValidateInDisplayedMonth = (
    weekReferenceDate: Date,
    displayedMonth: Date
): boolean =>
    isSameCalendarMonth(
        addDays(startOfISOWeek(weekReferenceDate), 4),
        displayedMonth
    );

const startOfCalendarDay = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Le bouton Valider n’apparaît qu’à partir du jeudi de la semaine ISO concernée. */
export const isAtLeastThursdayOfISOWeek = (
    weekReferenceDate: Date,
    referenceDate: Date = new Date()
): boolean => {
    const friday = addDays(startOfISOWeek(weekReferenceDate), 4);
    return (
        startOfCalendarDay(referenceDate).getTime() >=
        startOfCalendarDay(friday).getTime()
    );
};

